import { Injectable } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { SwUpdate, VersionReadyEvent } from "@angular/service-worker";
import { filter } from "rxjs";

import { SnackBarComponent } from "../../shared/components/snack-bar/snack-bar.component";
import { LocalStorageKeys } from "../../shared/helpers/app.constants";
import { BeforeInstallPromptEvent } from "../../shared/models/common.model";

@Injectable({ providedIn: 'root' })
export class PwaService {

  deferredPrompt: BeforeInstallPromptEvent | null = null;

  public get isEnabled(): boolean {
    return this.swUpdate?.isEnabled;
  }

  constructor(private readonly swUpdate: SwUpdate,
    private readonly snackBar: MatSnackBar
  ) {
    globalThis.addEventListener('beforeinstallprompt', (event) => {
      if (navigator.userAgent.includes('QuizoversApp')) {
        event.preventDefault();
        return;
      }
      event.preventDefault();
      this.deferredPrompt = event as BeforeInstallPromptEvent;
    });

    globalThis.addEventListener('appinstalled', () => {
      this.deferredPrompt = null;
    });

    setTimeout(() => {
      if (this.checkPWA()) {
        this.showInstallToast();
      }
    }, 5 * 60 * 1000);

    if (this.swUpdate.isEnabled) {
      this.versionUpdates().subscribe(() => {
        this.showUpdateToast();
      });
    }
  }

  showInstallToast(): void {
    const snackBarRef = this.snackBar.openFromComponent(SnackBarComponent, {
      data: {
        title: 'Install App',
        message: 'Install to access quickly',
        icon: 'bi-download',
        type: 'pwa',
        actionLabel: 'Install',
      },
      panelClass: 'pwa',
      horizontalPosition: 'center',
      verticalPosition: 'top',
    });

    snackBarRef.onAction().subscribe(() => {
      this.installPWA();
    });

    snackBarRef.afterDismissed().subscribe((info) => {
      if (!info.dismissedByAction) {
        this.cancelInstallation();
      }
    });
  }

  showUpdateToast(): void {
    const snackBarRef = this.snackBar.openFromComponent(SnackBarComponent, {
      data: {
        title: 'New version available!',
        message: 'Please update the app to the latest version to continue using the latest features and improvements.',
        icon: 'bi-arrow-repeat',
        type: 'pwa',
        pwaMode: 'update',
        actionLabel: 'Update',
      },
      panelClass: 'pwa',
      horizontalPosition: 'center',
      verticalPosition: 'top',
    });

    snackBarRef.onAction().subscribe(() => {
      this.activateUpdate();
    });
  }

  checkPWA(): boolean {
    const hasUserDismissed = localStorage.getItem(LocalStorageKeys.IsPWAInstalled) === 'true';
    const isInstalled = document.cookie.includes(`${LocalStorageKeys.IsPWAInstalled}=true`);
    const isStandalone = globalThis.matchMedia('(display-mode: standalone)').matches ||
      (globalThis.navigator as Navigator & { standalone?: boolean }).standalone === true;
    return (!hasUserDismissed && !isStandalone) && !isInstalled;
  }

  public installPWA(): boolean {
    if (!this.deferredPrompt) {
      console.warn('No install prompt available.');
      return true;
    }
    this.deferredPrompt.prompt();

    this.deferredPrompt.userChoice.then((choiceResult: { outcome: 'accepted' | 'dismissed'; platform: string }) => {
      this.deferredPrompt = null;
      if (choiceResult.outcome === 'accepted') {
        document.cookie = `${LocalStorageKeys.IsPWAInstalled}=true; path=/; max-age=31536000`;
        return true;
      } else {
        localStorage.setItem(LocalStorageKeys.IsPWAInstalled, 'true');
        return false;
      }
    }).catch((err) => {
      console.error('Error handling PWA installation prompt:', err);
      this.deferredPrompt = null;
    });
    return false;
  }

  public cancelInstallation(): void {
    localStorage.setItem(LocalStorageKeys.IsPWAInstalled, 'true');
  }

  versionUpdates() {
    return this.swUpdate.versionUpdates
      .pipe(filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'));
  }

  activateUpdate() {
    this.swUpdate.activateUpdate().then(() => document.location.reload());
  }
}